// Typed Hono RPC client. We import the API *types* from the shared package
// in spirit (the monorepo will share these), but at this scaffold stage the
// API app also exports a TS type via hono/client typings. Since we don't have
// a way to import the api app's AppType as a types-only module across the
// workspace easily without adding a tiny build step, we type the methods
// manually using the zod schemas from @shukan/shared.

import { hc } from "hono/client";
import { getAccessToken } from "./supabase";

const BASE = import.meta.env.VITE_API_URL ?? "";

type FetchFn = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

// Note: leaving the AppType as `any` here since Hono generates types from the
// route chain. The hand-typed zod schemas in @shukan/shared cover the response
// shapes used by the UI.
const rawClient = hc<any>(BASE, {
  fetch: (async (input, init) => {
    const token = await getAccessToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(input as Request | string, { ...init, headers });
  }) as FetchFn,
});

export const api = rawClient;

export async function fetchJson<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body}`);
  }
  return (await res.json()) as T;
}
